import { useRef, useState } from 'react'
import { showApiError, showSuccess } from '@/lib/utils/notifications'
import { RESIDENT_DOCUMENT_FILE_ACCEPT } from '../../constants/residentConstants'
import { addGuidance } from '../../services/residentApi'
import { RequiredAsterisk } from '../RequiredAsterisk'
import { ResidentDialogShell } from './ResidentDialogShell'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'

/** Angular `guidance-popup.component` for uploading guidance documents. */
export function GuidanceDialog({
  isOpen,
  defaultId,
  onClose,
  onUploaded,
  onUpdated,
}: {
  isOpen: boolean
  defaultId?: string | number | null
  onClose: () => void
  onUploaded?: () => void | Promise<void>
  onUpdated?: () => void | Promise<void>
}) {
  const [description, setDescription] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [pendingLargeFile, setPendingLargeFile] = useState<{
    selectedFile: File
    inputElement: HTMLInputElement
  } | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const submit = async () => {
    if (!defaultId || !file || submitting) return
    setSubmitting(true)
    try {
      const formData = new FormData()
      formData.append('guidance_documents', file)
      if (description) formData.append('order_no', description)
      formData.append('type', 'add')
      formData.append('document_name', file.name)
      await addGuidance(defaultId, formData)
      showSuccess('Guidance document saved successfully')
      setDescription('')
      setFile(null)
      if (onUploaded) await onUploaded()
      if (onUpdated) await onUpdated()
      onClose()
    } catch (err) {
      showApiError(err)
    } finally {
      setSubmitting(false)
    }
  }

  const handleConfirmLargeFile = () => {
    if (!pendingLargeFile) return
    const { selectedFile } = pendingLargeFile
    setPendingLargeFile(null)
    setFile(selectedFile)
  }

  const handleCancelLargeFile = () => {
    if (pendingLargeFile?.inputElement) {
      pendingLargeFile.inputElement.value = ''
    }
    setPendingLargeFile(null)
    setFile(null)
  }

  return (
    <>
      <ResidentDialogShell
        isOpen={isOpen}
        title="Guidance Document Upload"
        onClose={onClose}
        panelDataTestId="guidance-dialog-panel"
        footer={
          <div className="flex justify-end gap-2" data-testid="guidance-dialog-footer">
            <button
              type="button"
              disabled={submitting}
              onClick={onClose}
              data-testid="guidance-dialog-cancel-button"
              className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!file || submitting}
              onClick={() => void submit()}
              data-testid="guidance-dialog-submit-button"
              className="rounded-lg bg-button px-5 py-2 text-sm font-semibold text-white shadow-sm hover:brightness-110 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:shadow-none"
            >
              {submitting ? 'Uploading…' : 'Submit'}
            </button>
          </div>
        }
      >
        <div className="space-y-4" data-testid="guidance-dialog-body">
          <label className="block text-sm font-medium text-slate-700">
            Description
            <input
              className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              data-testid="guidance-dialog-description-input"
            />
          </label>
          <label className="text-sm font-medium text-slate-700">
            Guidance Document <RequiredAsterisk />
            <input
              ref={fileInputRef}
              className="mt-1 block w-full text-sm"
              type="file"
              accept={RESIDENT_DOCUMENT_FILE_ACCEPT}
              onChange={(e) => {
                const selected = e.target.files?.[0] || null
                // Angular selectFile (guidance-popup.component.ts:94-124): a file >10MB
                // requires an explicit confirm before it is accepted; on decline reset the input.
                if (selected && selected.size > 10 * 1024 * 1024) {
                  setPendingLargeFile({
                    selectedFile: selected,
                    inputElement: e.target,
                  })
                  return
                }
                setFile(selected)
              }}
              data-testid="guidance-dialog-file-input"
            />
          </label>
        </div>
      </ResidentDialogShell>

      <ConfirmDialog
        isOpen={pendingLargeFile != null}
        title="Large File"
        message={
          'WARNING: Your file is larger than 10MB.\n' +
          'We request you try to compress the file so it takes less storage.\n' +
          'However, if you are not able to compress it further, please continue.\n' +
          'Do you want to continue?'
        }
        primaryLabel="Yes"
        onPrimary={handleConfirmLargeFile}
        secondaryLabel="No"
        onSecondary={handleCancelLargeFile}
        onDismiss={handleCancelLargeFile}
        messageLayout="largeFile"
        primaryFirst
        actionStyle="application"
        sectionedChrome
      />
    </>
  )
}
